/**
 * Stock reconciliation page for physical counts and adjustment requests
 */

import React, { useState, useEffect, useCallback } from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import { Typography } from "@mui/material";
import { DmsPage, DmsTabber, DmsAltBox, DmsSelect, intl, fetchData, HTTP_STATUS, havePermissions } from "dmsbasereact";
import { Permissions } from "../../constants/Permissions";
import { DmsInstitutionSelect } from "../../components/DmsInstitutionSelect/DmsInstitutionSelect";
import { useStockReconciliation } from "./hooks/useStockReconciliation";
import { PhysicalCountSection } from "./sections/PhysicalCountSection";
import { AdjustmentFormSection } from "./sections/AdjustmentFormSection";
import { PendingAdjustmentsSection } from "./sections/PendingAdjustmentsSection";

export const StockReconciliationPage = () => {
    const isAdmin = havePermissions([Permissions.STOCK_MANAGEMENT_PAGE]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [institution, setInstitution] = useState(null);
    const [userInstitutions, setUserInstitutions] = useState([]);
    const [differences, setDifferences] = useState([]);

    const organizationBranchPk = institution?.organization_branch_pk || institution?.organization_branch?.pk;

    const {
        state: {
            stockItems,
            adjustmentTypes,
            pendingAdjustments
        },
        handlers: {
            loadAllData,
            createAdjustment
        }
    } = useStockReconciliation(organizationBranchPk);

    const fetchUserInstitutions = useCallback(async () => {
        try {
            const response = await fetchData("/get/organization_branch/by_person_pk", "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                const data = response.data?.data;
                if (data?.institutions?.length) return data.institutions;
                if (data?.institution) return [data.institution];
            }
            return [];
        } catch (err) {
            console.error("Error fetching institutions:", err);
            return [];
        }
    }, []);

    useEffect(() => {
        const initPage = async () => {
            setLoading(true);
            setError(null);

            if (isAdmin) {
                setLoading(false);
                return;
            }

            const insts = await fetchUserInstitutions();
            if (insts.length === 0) {
                setError(intl.get("NoInstitutionLinked").d("You are not linked to any institution"));
                setLoading(false);
                return;
            }

            setUserInstitutions(insts);
            setInstitution(insts[0]);
            setLoading(false);
        };

        initPage();
    }, [fetchUserInstitutions, isAdmin]);

    useEffect(() => {
        if (organizationBranchPk) {
            loadAllData();
        }
    }, [organizationBranchPk, loadAllData]);

    const handleInstitutionChange = useCallback((selectedValue) => {
        if (!selectedValue) {
            setInstitution(null);
            return;
        }
        setInstitution({ organization_branch_pk: selectedValue });
        setDifferences([]);
    }, []);

    const handleCompare = (diffs) => {
        setDifferences(diffs);
    };

    const handleSubmitAdjustment = async (adjustmentData) => {
        const success = await createAdjustment(adjustmentData);
        if (success) {
            const updatedDiffs = differences.filter(
                (d) => d.batch_pk !== adjustmentData.batch_pk
            );
            setDifferences(updatedDiffs);
        }
        return success;
    };

    const pageName = intl.get("StockReconciliation").d("Stock Reconciliation");

    if (loading) {
        return (
            <DmsPage name={pageName}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </DmsPage>
        );
    }

    if (error) {
        return (
            <DmsPage name={pageName}>
                <Alert severity="error">{error}</Alert>
            </DmsPage>
        );
    }

    const tabs = [
        {
            title: intl.get("PhysicalCount").d("Physical Count"),
            component: (
                <PhysicalCountSection
                    stockItems={stockItems}
                    onCompare={handleCompare}
                />
            ),
        },
        {
            title: intl.get("CreateAdjustment").d("Create Adjustment"),
            disabled: differences.length === 0,
            component: (
                <AdjustmentFormSection
                    differences={differences}
                    adjustmentTypes={adjustmentTypes}
                    onSubmit={handleSubmitAdjustment}
                />
            ),
        },
        {
            title: intl.get("PendingAdjustments").d("Pending Adjustments"),
            component: <PendingAdjustmentsSection adjustments={pendingAdjustments} onRefresh={loadAllData} />,
        },
    ];

    return (
        <DmsPage name={pageName}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                        {pageName}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    {isAdmin && (
                        <Box sx={{ mb: 2, maxWidth: 400 }}>
                            <DmsInstitutionSelect
                                label={intl.get("SelectInstitution").d("Select Institution")}
                                value={organizationBranchPk || ""}
                                onChange={handleInstitutionChange}
                                requireApproved={true}
                                needsAuthentication={true}
                            />
                        </Box>
                    )}
                    {!isAdmin && userInstitutions.length > 1 && (
                        <Box sx={{ mb: 2, maxWidth: 400 }}>
                            <DmsSelect
                                label={intl.get("SelectInstitution").d("Select Institution")}
                                value={organizationBranchPk || ""}
                                onChange={(value) => {
                                    const match = userInstitutions.find(
                                        (i) =>
                                            (i.organization_branch_pk || i.organization_branch?.pk) === Number(value)
                                    );
                                    setInstitution(match || null);
                                    setDifferences([]);
                                }}
                                options={userInstitutions.map((i) => ({
                                    value: i.organization_branch_pk || i.organization_branch?.pk,
                                    label: i.organization_branch?.name || i.name || `ID ${i.organization_branch_pk}`,
                                }))}
                                fullWidth
                            />
                        </Box>
                    )}
                    {organizationBranchPk ? (
                        <DmsTabber tabs={tabs} pageName={pageName} />
                    ) : (
                        isAdmin && (
                            <Alert severity="info">
                                {intl.get("SelectInstitutionToView").d("Select an institution to view stock reconciliation data")}
                            </Alert>
                        )
                    )}
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};
